import LegacyRetinaImage from 'components/LegacyRetinaImage'
import { ErrorMessage, Field, Formik } from 'formik'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'i18n'
import {
  generateFormValidation,
  getFormFieldByType,
  buildFormStepValues,
  buildFormStepEntry,
  generateCascadingDropdownInitialValues,
  generateCheckboxInitialValues,
  generateCustomFieldInitialValues,
  renderCascadingDropdowns,
} from './FormHelpers'
import { Checkbox, Dialog, FormControl, IconButton, TextField, Tooltip } from '@mui/material'
import { Turnstile } from '@marsidev/react-turnstile'
import { useTurnstile } from 'hooks/useTurnstile'
import { getTurnstileSiteKey } from 'util/getTurnstileSiteKey'
import {
  generateFormStepToken,
  verifyAndSubmitFormStep,
} from 'services/formStep'
import { doGet2 } from 'services/common'
import analytics from 'global/AnalyticsData'
import { scrollToTopOfCurrentStep } from 'services/break'
import trackMessage from 'util/track-message'
import { userContext } from 'global/context/sub-contexts/userContext'
import { UserVerification } from 'components/UserVerification/UserVerification'
import closeLogo from 'images/icon-close.svg'
import errorIcon from 'images/error-icon.svg'
import './FormStep.scss'

const FormStep = ({
  brk,
  breakId,
  step,
  terms,
  stepId,
  skipButton,
  nextStepButton,
  onEngage = function () {},
  onAwardPoints = function () {},
  handleInstantWinEvent = null,
}) => {
  const { t } = useTranslation(['form', 'common'])
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false)
  const [verificationData, setVerificationData] = useState(null)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [apiError, setApiError] = useState(null)
  const [refreshedCascadingDropdowns, setRefreshedCascadingDropdowns] =
    useState(null)
  const [additionalSlots, setAdditionalSlots] = useState([])
  const [slotErrors, setSlotErrors] = useState({})
  const [customFieldApiErrors, setCustomFieldApiErrors] = useState({})

  const userProfile = useContext(userContext).getProfile()

  const stepConfig = step.form
  const additionalSlotsConfig = stepConfig.additionalSlotsConfig
  const slotsEnabled = additionalSlotsConfig?.enabled === true
  const customFields = stepConfig.customFields || []

  const addSlot = () =>
    setAdditionalSlots(prev => [...prev, {}])

  const removeSlot = index => {
    setAdditionalSlots(prev => prev.filter((_, i) => i !== index))
    setSlotErrors({})
  }

  const updateSlot = (index, name, value) =>
    setAdditionalSlots(prev =>
      prev.map((slot, i) => (i === index ? { ...slot, [name]: value } : slot)),
    )

  const validateSlots = () => {
    if (!slotsEnabled || !additionalSlotsConfig?.fields) return true
    const errors = {}
    additionalSlots.forEach((slot, i) => {
      const slotErr = {}
      additionalSlotsConfig.fields.forEach(field => {
        if (field.required && !slot[field.name]?.trim()) {
          slotErr[field.name] = t('form:validation.required')
        }
      })
      if (Object.keys(slotErr).length) errors[i] = slotErr
    })
    setSlotErrors(errors)
    return Object.keys(errors).length === 0
  }

  const activeCascadingDropdowns =
    refreshedCascadingDropdowns ?? stepConfig.cascadingDropdowns
  const activeStepConfig = refreshedCascadingDropdowns
    ? { ...stepConfig, cascadingDropdowns: refreshedCascadingDropdowns }
    : stepConfig
  const dynamicValidationSchema = generateFormValidation(
    stepConfig,
    customFields,
    t,
  )

  const { turnstileRef } = useTurnstile()
  const FORM_TOKEN_SOURCE = 'FORM'
  const resolveStepPermalink = () => {
    const breakStep = Array.isArray(brk?.steps)
      ? brk.steps.find(item => item?.id === step?.id)
      : null

    return (
      step?.permalink ||
      step?.stepPermalink ||
      stepConfig?.permalink ||
      breakStep?.stepPermalink ||
      breakStep?.permalink ||
      brk?.permalink
    )
  }

  const computeInventoryConflict = values => {
    if (!activeCascadingDropdowns) return null
    let minAvailable = null
    for (const cdd of activeCascadingDropdowns) {
      const selectedValue = values[cdd.primaryField?.id]
      if (selectedValue) {
        const opt = cdd.primaryField.options?.find(o => o.value === selectedValue)
        if (opt?.inventorySize != null) {
          const available = opt.inventorySize - opt.occupiedCount
          if (minAvailable === null || available < minAvailable) minAvailable = available
        }
      }
      for (const dep of cdd.dependentFields || []) {
        const depValue = values[dep.id]
        if (depValue) {
          const opts = dep.optionsMap?.[values[dep.dependsOn]] || []
          const opt = opts.find(o => o.value === depValue)
          if (opt?.inventorySize != null) {
            const available = opt.inventorySize - opt.occupiedCount
            if (minAvailable === null || available < minAvailable) minAvailable = available
          }
        }
      }
    }
    if (minAvailable === null) return null
    if (additionalSlots.length + 1 > minAvailable) {
      return { available: minAvailable, excess: additionalSlots.length + 1 - minAvailable }
    }
    return null
  }

  const handleFormSubmit = async values => {
    setApiError(null)

    if (!validateSlots()) return

    if (computeInventoryConflict(values)) return

    try {
      const token = turnstileRef.current?.getResponse()
      if (!token) {
        setApiError(t('form:error.verificationIncomplete'))
        return
      }

      const { values: formStepValues, inventorySelections } =
        buildFormStepValues(activeStepConfig, values)

      const formStepEntry = buildFormStepEntry(
        activeCascadingDropdowns,
        values,
        additionalSlots,
        slotsEnabled,
        customFields,
      )

      setVerificationData({
        // form-step submission fields
        ...(formStepEntry ? { formStepEntry } : {}),
        breakId,
        stepId: step.stepId || step.originalStepId, // NUMERIC StepBuilderStep id
        values: formStepValues,
        inventorySelections,
        turnstileToken: token,
        turnstileTokenType: 'visible',
        // email-verification fields
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        tokenSource: FORM_TOKEN_SOURCE,
        tokenSourceId: step?.stepId || step.id,
        permalink: resolveStepPermalink(),
      })
      setVerificationDialogOpen(true)
    } catch (e) {
      // Nothing above calls the API — the only way to throw here is a bug while
      // building the payload. This used to be swallowed with a console.warn, so a
      // crash left the user staring at a Submit button that did nothing (BR-5090:
      // an undefined helper threw here and the submission silently vanished).
      // Surface it instead: the user gets a retry, and we keep a real error log.
      console.error('form-step: failed to prepare submission', e)
      setApiError(t('form:error.prepareSubmission'))
    }
  }

  const handleVerificationSuccess = async ({ values, confirmed }) => {
    if (!confirmed) return

    const result = values?.detail || values

    onEngage && onEngage()
    onAwardPoints && onAwardPoints()

    analytics.stepEvent('step_interaction_form_info_submission', step, {})

    setVerificationDialogOpen(false)
    setVerificationData(null)
    setFormSubmitted(true)
    scrollToTopOfCurrentStep(step.id)

    setTimeout(() => {
      handleInstantWinEvent &&
        handleInstantWinEvent({
          eventName: `leadgen_submit`.toLowerCase(),
          context: { form: result, stepId: step.id },
        })
    }, 400)
  }

  const handleVerificationError = async error => {
    const errorMessage = error?.detail || error?.message
    trackMessage(
      `Form submission verification: FAILURE. permalink: ${
        window && window?.location?.href ? window?.location?.href : 'unknown'
      }. User: ${userProfile?.email}. Detail: ${JSON.stringify(errorMessage)}`,
    )

    if (error?.status === 400) {
      // /form-step/verify-and-submit reports missing required fields by key;
      // the legacy leadgen flow reported them by label — support both.
      const keyMatch = errorMessage?.match(/Required field missing: (.+)/)
      const labelMatch = errorMessage?.match(/Required field '(.+)' is missing/)
      const matchingField =
        (keyMatch && customFields.find(f => f.name === keyMatch[1].trim())) ||
        (labelMatch && customFields.find(f => f.label === labelMatch[1]))
      if (matchingField) {
        setCustomFieldApiErrors({
          [matchingField.name]: t('form:validation.fieldRequired', {
            label: matchingField.label || matchingField.name,
          }),
        })
        setVerificationDialogOpen(false)
        setVerificationData(null)
        return
      }
      setApiError(errorMessage || t('form:error.submission'))
    } else if (error?.status === 409) {
      setApiError(t('form:error.optionUnavailable'))
      try {
        const response = await doGet2(`/step/${step.id}`)
        const freshStep = response?.detail || response
        const freshCascading =
          freshStep?.form?.cascadingDropdowns ||
          freshStep?.cascadingDropdowns ||
          null
        if (freshCascading) {
          setRefreshedCascadingDropdowns(freshCascading)
        }
      } catch (fetchError) {
        console.warn('BR: Failed to refresh step after 409', fetchError)
      }
    } else {
      setApiError(errorMessage || t('form:error.submission'))
    }

    setVerificationDialogOpen(false)
    setVerificationData(null)
  }

  const clearCustomFieldApiError = name => {
    if (customFieldApiErrors[name]) {
      setCustomFieldApiErrors(prev => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const handleDialogClose = () => {
    setVerificationDialogOpen(false)
    setVerificationData(null)
    turnstileRef.current?.reset()
  }

  return (
    <section className="form-email-step">
      <div
        className={`step-content ${step.backgroundImage ? ' with-shadow' : ''}`}
      >
        {formSubmitted === false && (
          <div className="form-email-form-body-container">
            <div className="intro">
              {stepConfig.headline && (
                <h1
                  className={`step-content__title form-email-form-title`}
                  dangerouslySetInnerHTML={{
                    __html: stepConfig.headline,
                  }}
                />
              )}
              {stepConfig.introCopy && (
                <h2
                  className={`form-email-form-subhead step-subcopy`}
                  dangerouslySetInnerHTML={{ __html: stepConfig.introCopy }}
                />
              )}
              {stepConfig.headerImage && (
                <LegacyRetinaImage
                  image={stepConfig.headerImage}
                  className={`form-email-form-image`}
                />
              )}
            </div>
            <Formik
              initialValues={{
                permalink: resolveStepPermalink(),
                turnstileToken: '',
                turnstileTokenType: '',
                firstName: '',
                lastName: '',
                fullName: '',
                email: '',
                phoneNumber: '',
                ...generateCheckboxInitialValues(stepConfig.checkboxes),
                ...generateCascadingDropdownInitialValues(
                  stepConfig.cascadingDropdowns,
                ),
                ...generateCustomFieldInitialValues(customFields),
              }}
              validationSchema={dynamicValidationSchema}
              validateOnBlur={false}
              validateOnChange={true}
              validateOnMount={true}
              onSubmit={async values => {
                console.log('Form email form submitted!')
                await handleFormSubmit(values)
              }}
            >
              {formik => {
                return (
                  <>
                    <Dialog
                      onClose={(_, reason) =>
                        reason !== 'backdropClick' && handleDialogClose()
                      }
                      open={verificationDialogOpen}
                    >
                      <IconButton
                        aria-label={t('common:close')}
                        onClick={handleDialogClose}
                        sx={{
                          right: 8,
                          top: 8,
                          alignSelf: 'end',
                        }}
                      >
                        <img src={closeLogo} alt={t('common:close')} />
                      </IconButton>
                      {verificationData && (
                        <UserVerification
                          hasTurnstile={false}
                          onConfirm={handleVerificationSuccess}
                          onValidationError={handleVerificationError}
                          customGenerateTokenService={
                            generateFormStepToken
                          }
                          customValidateTokenService={
                            verifyAndSubmitFormStep
                          }
                          tokenServicePayload={verificationData}
                          email={verificationData.email}
                          firstName={
                            verificationData.firstName ||
                            verificationData.fullName
                          }
                          lastName={verificationData.lastName}
                          hideSenderEmail={true}
                        />
                      )}
                    </Dialog>
                    <form>
                      <div className="fields">
                        {stepConfig.fields &&
                          stepConfig.fields.map((item, i) => {
                            return getFormFieldByType({
                              field: item,
                              formik,
                              index: i,
                              t,
                            })
                          })}

                        {customFields.map(field => {
                          const clientError = formik.errors[field.name] && formik.touched[field.name]
                            ? formik.errors[field.name]
                            : null
                          const serverError = customFieldApiErrors[field.name]
                          const errorToShow = serverError || clientError
                          return (
                            <FormControl
                              key={field.name}
                              className={`field field-${field.name}`}
                              style={{ position: 'relative' }}
                            >
                              <TextField
                                variant="filled"
                                label={`${field.label}${field.required ? ' *' : ''}`}
                                value={formik.values[field.name] || ''}
                                name={field.name}
                                onChange={e => {
                                  formik.setFieldValue(field.name, e.target.value)
                                  clearCustomFieldApiError(field.name)
                                }}
                                onBlur={() => formik.setFieldTouched(field.name, true)}
                                fullWidth
                              />
                              {errorToShow && (
                                <div className="error">{errorToShow}</div>
                              )}
                              {errorToShow && (
                                <img className="error-icon" src={errorIcon} alt="" />
                              )}
                            </FormControl>
                          )
                        })}

                        {activeCascadingDropdowns && (
                          <div className="cascading-dropdowns">
                            {renderCascadingDropdowns(
                              activeCascadingDropdowns,
                              formik,
                              {
                                showRemainingInventory: stepConfig.showRemainingInventory,
                                remainingInventoryThreshold: stepConfig.remainingInventoryThreshold,
                                remainingInventoryCopy: stepConfig.remainingInventoryCopy,
                              },
                              t,
                            )}
                          </div>
                        )}
                      </div>

                      {slotsEnabled && (
                        <div className="additional-slots">
                          {additionalSlots.map((slot, slotIndex) => (
                            <div key={slotIndex} className="additional-slot">
                              <div className="additional-slot-header">
                                <span className="additional-slot-title">
                                  {additionalSlotsConfig.slotTitle} {slotIndex + 1}
                                </span>
                                <button
                                  type="button"
                                  className="additional-slot-remove"
                                  onClick={() => removeSlot(slotIndex)}
                                >
                                  {t('form:slots.remove')}
                                </button>
                              </div>
                              <div className="additional-slot-fields">
                                {additionalSlotsConfig.fields.map(field => (
                                  <FormControl
                                    key={field.name}
                                    className="field"
                                    style={{ position: 'relative' }}
                                  >
                                    <TextField
                                      variant="filled"
                                      label={`${field.label}${field.required ? ' *' : ''}`}
                                      value={slot[field.name] || ''}
                                      onChange={e =>
                                        updateSlot(slotIndex, field.name, e.target.value)
                                      }
                                      error={!!slotErrors[slotIndex]?.[field.name]}
                                      fullWidth
                                    />
                                    {slotErrors[slotIndex]?.[field.name] && (
                                      <div className="slot-field-error">
                                        {slotErrors[slotIndex][field.name]}
                                      </div>
                                    )}
                                  </FormControl>
                                ))}
                              </div>
                            </div>
                          ))}

                          {(() => {
                            const conflict = computeInventoryConflict(formik.values)
                            if (!conflict) return null
                            return (
                              <div className="slot-inventory-error">
                                {t('form:slots.inventoryError', {
                                  count: conflict.available,
                                  slotsForOthers: conflict.available - 1,
                                  added: additionalSlots.length,
                                  excess: conflict.excess,
                                  noun:
                                    additionalSlotsConfig.slotTitle?.toLowerCase() ||
                                    t('form:slots.defaultNoun'),
                                })}
                              </div>
                            )
                          })()}

                          {(() => {
                            const atMax =
                              additionalSlotsConfig.maxAdditionalSlots != null &&
                              additionalSlots.length >= additionalSlotsConfig.maxAdditionalSlots

                            const inventoryFull = (() => {
                              if (!activeCascadingDropdowns) return false
                              for (const cdd of activeCascadingDropdowns) {
                                const selectedValue = formik.values[cdd.primaryField?.id]
                                if (selectedValue) {
                                  const opt = cdd.primaryField.options?.find(o => o.value === selectedValue)
                                  if (opt?.inventorySize != null) {
                                    const available = opt.inventorySize - opt.occupiedCount
                                    if (additionalSlots.length + 1 >= available) return true
                                  }
                                }
                                for (const dep of cdd.dependentFields || []) {
                                  const depValue = formik.values[dep.id]
                                  if (depValue) {
                                    const opts = dep.optionsMap?.[formik.values[dep.dependsOn]] || []
                                    const opt = opts.find(o => o.value === depValue)
                                    if (opt?.inventorySize != null) {
                                      const available = opt.inventorySize - opt.occupiedCount
                                      if (additionalSlots.length + 1 >= available) return true
                                    }
                                  }
                                }
                              }
                              return false
                            })()

                            const isDisabled = atMax || inventoryFull
                            const tooltipText = atMax
                              ? additionalSlotsConfig.maxAdditionalSlotsTooltip ||
                                t('form:slots.maxReachedTooltip', {
                                  max: additionalSlotsConfig.maxAdditionalSlots,
                                  noun:
                                    additionalSlotsConfig.slotTitle?.toLowerCase() ||
                                    t('form:slots.defaultNoun'),
                                })
                              : inventoryFull
                              ? t('form:slots.inventoryFullTooltip')
                              : ''

                            return (
                              <Tooltip
                                title={tooltipText}
                                arrow
                                placement="right"
                                disableHoverListener={!isDisabled}
                                disableFocusListener={!isDisabled}
                                disableTouchListener={!isDisabled}
                              >
                                <span>
                                  <button
                                    type="button"
                                    className={`add-slot-button${isDisabled ? ' add-slot-button--disabled' : ''}`}
                                    onClick={isDisabled ? undefined : addSlot}
                                    disabled={isDisabled}
                                  >
                                    {additionalSlotsConfig.addButtonCopy ||
                                      t('form:slots.addCta')}
                                    <span className="add-slot-icon">+</span>
                                  </button>
                                </span>
                              </Tooltip>
                            )
                          })()}
                        </div>
                      )}

                      <div className="confirmations">
                        {stepConfig.checkboxes &&
                          stepConfig.checkboxes.map((item, i) => {
                            return (
                              <Field name={item.id} key={i}>
                                {({ field, meta }) => {
                                  return (
                                    <>
                                      <div
                                        className="form-email-confirmation"
                                        key={i}
                                      >
                                        <Checkbox
                                          id={field.name}
                                          name={field.name}
                                          checked={Boolean(
                                            formik.values[item.id],
                                          )}
                                          onChange={e => {
                                            formik.setFieldValue(
                                              field.name,
                                              e.target.checked,
                                            )
                                          }}
                                          onBlur={field.onBlur}
                                        />
                                        <label
                                          htmlFor={field.name}
                                          dangerouslySetInnerHTML={{
                                            __html: item.label,
                                          }}
                                        />
                                      </div>
                                      {meta.error && (
                                        <span className="form-email-confirmation-error">
                                          <ErrorMessage name={field.name} />
                                          <br />
                                          <br />
                                        </span>
                                      )}
                                    </>
                                  )
                                }}
                              </Field>
                            )
                          })}
                        {apiError && (
                          <div style={{ fontSize: '16px' }} className="error">
                            {apiError}
                          </div>
                        )}
                      </div>
                      <Turnstile
                        ref={turnstileRef}
                        options={{ refreshExpired: 'manual' }}
                        siteKey={getTurnstileSiteKey()}
                        onExpire={() => turnstileRef.current?.reset()}
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      />
                      <button
                        type="submit"
                        className={`submit-button-${
                          formik.dirty === false ||
                          Object.keys(formik.errors).length > 0
                            ? 'form-valid'
                            : 'form-invalid'
                        }`}
                        onClick={e => {
                          e.preventDefault()
                          formik.handleSubmit()
                        }}
                      >
                        {t('common:submit')}
                      </button>
                      {terms && (
                        <span
                          className="terms"
                          dangerouslySetInnerHTML={{ __html: `*${terms}` }}
                        ></span>
                      )}
                    </form>
                  </>
                )
              }}
            </Formik>
          </div>
        )}

        {formSubmitted === true && (
          <div className={`form-email-form-thank-you-container thank-you`}>
            {stepConfig.atSubmitHeadline && (
              <h1
                dangerouslySetInnerHTML={{
                  __html: stepConfig.atSubmitHeadline,
                }}
              />
            )}

            {stepConfig.atSubmitCopy && (
              <p
                dangerouslySetInnerHTML={{
                  __html: stepConfig.atSubmitCopy,
                }}
              />
            )}

            {stepConfig.atSubmitImage && (
              <LegacyRetinaImage
                image={stepConfig.atSubmitImage}
                className={`form-email-form-image`}
              />
            )}
          </div>
        )}
        {skipButton}
        {nextStepButton}
      </div>
    </section>
  )
}

export default FormStep
